import { ZuruflexApiError } from '@zuruflex/api-client';
import { contentPlainText, diffLines, hasChanges } from '@zuruflex/core';
import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import type { ContentDoc, ContentPageAdmin, ContentRevisionSummary } from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  ContentDocument,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from '@zuruflex/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';

export interface VersionsPanelProps {
  slug: string;
  /** The version counter the editor loaded, sent back so a restore cannot clobber a newer save. */
  pageVersion: number;
  /** What is in the editor now, for "compared with what you have". */
  editorDoc: ContentDoc;
  /** Unsaved text would be replaced by a restore, so say so first. */
  dirty: boolean;
  /** Called with the page after a restore so the editor can load it. */
  onRestored: (page: ContentPageAdmin) => void;
}

/**
 * Every version that has been on the website, with the words, a comparison
 * against what is being edited, and a way back.
 *
 * "Restore" fills the working copy and nothing else: it is a draft like any
 * other edit, so an owner still reads it and publishes it, and it becomes
 * the next version instead of rewriting history. For `partner-terms` the
 * version numbers are the ones partners agreed to, which is why the older
 * text has to stay readable.
 */
export function VersionsPanel({ slug, pageVersion, editorDoc, dirty, onRestored }: VersionsPanelProps) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const [viewing, setViewing] = useState<ContentRevisionSummary | null>(null);

  const { data } = useQuery({
    queryKey: ['content', slug, 'revisions'],
    queryFn: () => api.contentRevisions(slug),
  });

  return (
    <section className="space-y-2 rounded-2xl border border-border-default p-4">
      <h2 className="text-sm font-semibold">{t('admin:content.versions.title')}</h2>
      {data?.length ? (
        <ul className="space-y-1">
          {data.slice(0, 8).map((revision) => (
            <li key={revision.version}>
              <button
                type="button"
                onClick={() => setViewing(revision)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-surface-hover',
                )}
              >
                <span className="flex items-center gap-2">
                  <span className="font-medium tabular-nums">v{revision.version}</span>
                  {revision.isCurrent ? <Badge variant="success">{t('admin:content.versions.live')}</Badge> : null}
                </span>
                <span className="text-right text-xs text-text-muted">
                  {formatDateTimeIn(revision.publishedAt, locale)}
                  {revision.publishedByName ? <span className="block">{revision.publishedByName}</span> : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-text-muted">{t('admin:content.versions.none')}</p>
      )}

      {viewing ? (
        <VersionDialog
          slug={slug}
          revision={viewing}
          pageVersion={pageVersion}
          editorDoc={editorDoc}
          dirty={dirty}
          onClose={() => setViewing(null)}
          onRestored={(page) => {
            setViewing(null);
            onRestored(page);
          }}
        />
      ) : null}
    </section>
  );
}

function VersionDialog({
  slug,
  revision,
  pageVersion,
  editorDoc,
  dirty,
  onClose,
  onRestored,
}: {
  slug: string;
  revision: ContentRevisionSummary;
  pageVersion: number;
  editorDoc: ContentDoc;
  dirty: boolean;
  onClose: () => void;
  onRestored: (page: ContentPageAdmin) => void;
}) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);

  const { data, error } = useQuery({
    queryKey: ['content', slug, 'revision', revision.version],
    queryFn: () => api.contentRevision(slug, revision.version),
  });

  const restore = useMutation({
    mutationFn: () => api.restoreContentRevision(slug, revision.version, pageVersion),
    onSuccess: onRestored,
  });

  const lines = useMemo(
    () => (data ? diffLines(contentPlainText(data.doc), contentPlainText(editorDoc)) : []),
    [data, editorDoc],
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t('admin:content.versions.heading', { version: revision.version })}
            {revision.isCurrent ? ` · ${t('admin:content.versions.live')}` : ''}
          </DialogTitle>
          <DialogDescription>
            {formatDateTimeIn(revision.publishedAt, locale)}
            {revision.publishedByName ? ` · ${revision.publishedByName}` : ''}
          </DialogDescription>
        </DialogHeader>

        {error ? <Alert variant="danger">{t('common:error.unexpected')}</Alert> : null}

        {data ? (
          <Tabs defaultValue="text">
            <TabsList>
              <TabsTrigger value="text">{t('admin:content.versions.text')}</TabsTrigger>
              <TabsTrigger value="changes">{t('admin:content.versions.changes')}</TabsTrigger>
            </TabsList>
            <TabsContent value="text" className="max-h-[50vh] overflow-y-auto pt-4">
              <h3 className="mb-3 text-lg font-bold">{data.title}</h3>
              <ContentDocument doc={data.doc} size="sm" />
            </TabsContent>
            <TabsContent value="changes" className="max-h-[50vh] overflow-y-auto pt-4">
              <p className="mb-3 text-xs text-text-muted">{t('admin:content.versions.compare')}</p>
              {hasChanges(lines) ? (
                <div className="space-y-px font-mono text-xs leading-relaxed">
                  {lines
                    .filter((line) => line.text.trim() !== '' || line.type !== 'same')
                    .map((line, index) => (
                      <div
                        key={index}
                        className={cn(
                          'whitespace-pre-wrap rounded px-2 py-0.5',
                          line.type === 'added' && 'bg-success/10 text-success',
                          line.type === 'removed' && 'bg-danger/10 text-danger line-through',
                          line.type === 'same' && 'text-text-muted',
                        )}
                      >
                        <span aria-hidden className="mr-2 select-none">
                          {line.type === 'added' ? '+' : line.type === 'removed' ? '−' : ' '}
                        </span>
                        {line.text}
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-text-muted">{t('admin:content.versions.identical')}</p>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <p className="text-sm text-text-muted">{t('common:state.loading', { defaultValue: '…' })}</p>
        )}

        {dirty ? <Alert variant="warning">{t('admin:content.versions.dirty')}</Alert> : null}
        {restore.error ? (
          <Alert variant="danger">
            {restore.error instanceof ZuruflexApiError ? restore.error.message : t('common:error.unexpected')}
          </Alert>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:action.close', { defaultValue: 'Close' })}
          </Button>
          <Button
            variant="outline"
            disabled={!data || revision.isCurrent}
            isLoading={restore.isPending}
            onClick={() => restore.mutate()}
          >
            {t('admin:content.versions.restore')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
